/**
 * Hooks Library Page - Reference Implementations
 *
 * Showcases the @edge-stream/hooks library as reference implementations.
 * These are NOT registered to any pipeline, but demonstrate the hook system's
 * capability for logging, API integration, and observability.
 */

import { useState } from 'react'
import React from 'react'
import { ErrorBoundary } from './components'
import { NoteIcon, GlobeIcon, BarChartIcon, ClipboardIcon, LockIcon, BookOpenIcon, CloseIcon, CheckIcon, HooksDetailedIcon } from '@bizfirst/common-react'

interface HookExample {
  name: string;
  type: string;
  description: string;
  icon: React.ReactNode;
  config: string;
  useCase: string[];
}

const hooksLibrary: HookExample[] = [
  {
    name: 'LogHook',
    type: 'Diagnostic',
    description: 'Logs all messages flowing through the pipeline with optional filtering and payload capture.',
    icon: <NoteIcon />,
    useCase: [
      'Debug message flow in pipelines',
      'Audit trail recording',
      'Performance monitoring',
      'Compliance logging',
    ],
    config: `new LogHook({
  label: 'PIPELINE_NAME',
  filter: (context) => context.envelope.meta.topic === 'important/*',
  includePayload: true  // Include full message data
})`,
  },
  {
    name: 'WebMcpHook',
    type: 'Integration',
    description:
      'Generic REST API integration hook for calling external services, webhooks, and third-party APIs.',
    icon: <GlobeIcon />,
    useCase: [
      'Call external APIs during pipeline execution',
      'Send webhook notifications',
      'Third-party service integration',
      'Conditional forwarding to external systems',
    ],
    config: `new WebMcpHook({
  endpoint: 'https://api.example.com/webhook',
  method: 'POST',
  headers: { 'X-API-Key': 'secret' },
  transform: (context) => ({
    messageId: context.envelope.meta.id,
    topic: context.envelope.meta.topic,
    timestamp: new Date().toISOString()
  }),
  timeout: 5000,
  retries: 3,
  condition: (context) => context.envelope.meta.type === 'critical'
})`,
  },
  {
    name: 'SplunkLogHook',
    type: 'Observability',
    description:
      'Specialized hook for streaming messages to Splunk HTTP Event Collector (HEC) for centralized logging.',
    icon: <BarChartIcon />,
    useCase: [
      'Real-time log streaming to Splunk',
      'Centralized observability and monitoring',
      'Compliance and audit trail',
      'Performance metrics collection',
    ],
    config: `new SplunkLogHook({
  hecUrl: 'https://splunk.company.com:8088/services/collector',
  hecToken: 'YOUR_HEC_TOKEN',  // Store in env variable!
  sourcetype: 'edgestream:pipeline',
  source: 'edgestream-production',
  index: 'main',
  batchSize: 100,
  flushInterval: 5000  // Flush every 5 seconds or when batch full
})`,
  },
  {
    name: 'ElasticsearchHook',
    type: 'Observability',
    description:
      'Indexes all pipeline messages into Elasticsearch for full-text search, analytics dashboards, and long-term retention.',
    icon: <GlobeIcon />,
    useCase: [
      'Full-text search across all pipeline messages',
      'Kibana dashboards and visualisations',
      'Long-term message retention and replay',
      'Cross-pipeline analytics and correlation',
    ],
    config: `new ElasticsearchHook({
  endpoint: 'https://elasticsearch.company.com:9200',
  index: 'edgestream-pipeline',
  username: 'elastic',         // Store in env variable!
  password: 'YOUR_PASSWORD',   // Store in env variable!
  batchSize: 100,
  flushInterval: 5000
})`,
  },
  {
    name: 'DatadogHook',
    type: 'Observability',
    description:
      'Forwards pipeline events to Datadog as structured logs with custom tags for cloud-native infrastructure monitoring.',
    icon: <BarChartIcon />,
    useCase: [
      'Infrastructure and APM monitoring',
      'Custom metrics and dashboards in Datadog',
      'Alerting on pipeline error rates',
      'Correlate pipeline events with host metrics',
    ],
    config: `new DatadogHook({
  apiKey: process.env.DD_API_KEY!,
  site: 'datadoghq.com',       // or 'datadoghq.eu'
  service: 'edgestream',
  env: 'production',
  tags: { team: 'platform', app: 'bizfirst' },
  batchSize: 100,
  flushInterval: 5000
})`,
  },
  {
    name: 'NewRelicHook',
    type: 'Observability',
    description:
      'Sends custom events to New Relic for full-stack observability. Requires a secure server-side proxy — see security note.',
    icon: <BarChartIcon />,
    useCase: [
      'Full-stack observability with New Relic APM',
      'Custom event dashboards and NRQL queries',
      'Distributed tracing across services',
      'Alerting and anomaly detection',
    ],
    config: `// Note: route through a server-side proxy to protect your license key.
// Contact community.bizfirst.com for the BizFirst proxy service.
new NewRelicHook({
  licenseKey: process.env.NR_LICENSE_KEY!, // Never expose client-side
  accountId: 'YOUR_ACCOUNT_ID',
  appName: 'EdgeStream',
  tags: { environment: 'production' },
  batchSize: 100,
  flushInterval: 5000
})`,
  },
  {
    name: 'SumoLogicHook',
    type: 'Observability',
    description:
      'Streams structured logs and security events to Sumo Logic using its HTTP Collector for advanced log analytics.',
    icon: <ClipboardIcon />,
    useCase: [
      'Advanced log analytics and threat detection',
      'Security event correlation (SIEM)',
      'Compliance reporting and audit trails',
      'Real-time alerting on anomalies',
    ],
    config: `new SumoLogicHook({
  collectorUrl: 'https://collectors.sumologic.com/receiver/v1/http/YOUR_TOKEN',
  source: 'edgestream',
  sourceCategory: 'pipeline/messages',
  metadata: { environment: 'production', team: 'platform' },
  batchSize: 100,
  flushInterval: 5000
})`,
  },
  {
    name: 'GraylogHook',
    type: 'Observability',
    description:
      'Sends pipeline messages to Graylog in GELF format for cost-effective centralized logging on open-source or enterprise deployments.',
    icon: <NoteIcon />,
    useCase: [
      'Self-hosted centralised log management',
      'GELF-format structured logging',
      'On-premise compliance requirements',
      'Cost-effective alternative to SaaS log platforms',
    ],
    config: `new GraylogHook({
  gelfUrl: 'http://graylog.company.com:12201/gelf',
  facility: 'edgestream',
  level: 'info',
  additionalFields: {
    environment: 'production',
    app: 'bizfirst'
  },
  batchSize: 50,
  flushInterval: 3000
})`,
  },
];

const GlobalObservabilityPattern = `
// Global observability - capture ALL messages going to Splunk
// This is separate from pipeline hooks - runs at stream level

import { createEdgeStream } from 'edge-stream-js';
import { SplunkLogHook } from '@edge-stream/hooks';

const stream = createEdgeStream();

const splunkHook = new SplunkLogHook({
  hecUrl: process.env.SPLUNK_HEC_URL!,
  hecToken: process.env.SPLUNK_HEC_TOKEN!,
});

// Subscribe to ALL messages (not just pipeline-specific)
stream.on('message:received', async (envelope) => {
  await splunkHook.execute({ envelope } as any);
});

// This captures observability from every hook execution,
// every message, every transformation - system-wide visibility
`;

export function HooksLibraryPage() {
  const [selectedHook, setSelectedHook] = useState<string | null>(null);
  const selected = hooksLibrary.find((h) => h.name === selectedHook);

  return (
    <ErrorBoundary>
      <div className="hooks-library-page">
        <div className="hooks-library-header">
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><HooksDetailedIcon /> Hooks Library - Reference Implementations</h1>
        <p>
          These hooks demonstrate the capability of the EdgeStream hook system. They are production-ready
          and can be configured and registered in your pipelines.
        </p>
      </div>

      <div className="hooks-library-grid">
        {hooksLibrary.map((hook) => (
          <div
            key={hook.name}
            className={`hook-card ${selectedHook === hook.name ? 'active' : ''}`}
            onClick={() => setSelectedHook(selectedHook === hook.name ? null : hook.name)}
          >
            <div className="hook-card-icon">{hook.icon}</div>
            <div className="hook-card-content">
              <h3>{hook.name}</h3>
              <span className="hook-type-badge" data-type={hook.type}>{hook.type}</span>
              <p>{hook.description}</p>
            </div>
          </div>
        ))}
      </div>

      {selected && (
        <div className="hooks-library-detail">
          <div className="detail-header">
            <h2>
              {selected.icon} {selected.name}
            </h2>
            <button className="close-btn" onClick={() => setSelectedHook(null)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CloseIcon />
            </button>
          </div>

          <div className="detail-section">
            <h3>Use Cases</h3>
            <ul>
              {selected.useCase.map((useCase, i) => (
                <li key={i}><CheckIcon /> {useCase}</li>
              ))}
            </ul>
          </div>

          <div className="detail-section">
            <h3>Configuration Example</h3>
            <pre className="code-block">
              <code>{selected.config}</code>
            </pre>
          </div>
        </div>
      )}

      <div className="hooks-library-section">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><GlobeIcon /> Global Observability Pattern (Recommended for Splunk)</h2>
        <p>
          Instead of hooking individual pipelines, implement observability at the stream level to capture
          ALL messages and hook executions:
        </p>
        <pre className="code-block large">
          <code>{GlobalObservabilityPattern.trim()}</code>
        </pre>
        <div className="pattern-explanation">
          <h3>Why Global Observability?</h3>
          <ul>
            <li>
              <strong>Complete Visibility</strong>: Captures every message across all pipelines and hooks
            </li>
            <li>
              <strong>System-Wide Metrics</strong>: Track throughput, latency, errors from all sources
            </li>
            <li>
              <strong>Single Integration</strong>: Configure Splunk once instead of per-pipeline
            </li>
            <li>
              <strong>Non-Intrusive</strong>: Doesn't require modifying pipeline configurations
            </li>
            <li>
              <strong>Flexible Filtering</strong>: Filter at the stream level, not hook level
            </li>
          </ul>
        </div>
      </div>

      <div className="hooks-library-section">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><ClipboardIcon /> Hook Architecture</h2>
        <div className="architecture-grid">
          <div className="arch-item">
            <h3>Hook Lifecycle</h3>
            <ol>
              <li>Pipeline receives message</li>
              <li>Creates context with envelope</li>
              <li>Executes hooks by priority (low→high)</li>
              <li>Each hook returns HookResult</li>
              <li>Result controls pipeline flow</li>
            </ol>
          </div>
          <div className="arch-item">
            <h3>Execution Order</h3>
            <table className="priority-table">
              <tbody>
                <tr>
                  <td>Priority 10</td>
                  <td>LogHook</td>
                </tr>
                <tr>
                  <td>Priority 15</td>
                  <td>SplunkLogHook</td>
                </tr>
                <tr>
                  <td>Priority 15</td>
                  <td>ElasticsearchHook</td>
                </tr>
                <tr>
                  <td>Priority 15</td>
                  <td>DatadogHook</td>
                </tr>
                <tr>
                  <td>Priority 15</td>
                  <td>NewRelicHook</td>
                </tr>
                <tr>
                  <td>Priority 15</td>
                  <td>SumoLogicHook</td>
                </tr>
                <tr>
                  <td>Priority 15</td>
                  <td>GraylogHook</td>
                </tr>
                <tr>
                  <td>Priority 50</td>
                  <td>WebMcpHook</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="arch-item">
            <h3>Design Principles</h3>
            <ul>
              <li><CheckIcon />Single responsibility</li>
              <li><CheckIcon />Non-blocking execution</li>
              <li><CheckIcon />Graceful degradation</li>
              <li><CheckIcon />Configurable behavior</li>
              <li><CheckIcon />Error isolation</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="hooks-library-section">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><LockIcon /> Security Best Practices</h2>
        <div className="security-grid">
          <div className="security-item">
            <h3>Token Management</h3>
            <ul>
              <li>Store HEC tokens in environment variables</li>
              <li>Never commit tokens to version control</li>
              <li>Use .env.local for development</li>
              <li>Rotate tokens regularly</li>
            </ul>
          </div>
          <div className="security-item">
            <h3>Network Security</h3>
            <ul>
              <li>Always use HTTPS for remote endpoints</li>
              <li>Validate SSL certificates</li>
              <li>Set appropriate timeouts</li>
              <li>Implement rate limiting</li>
            </ul>
          </div>
          <div className="security-item">
            <h3>Data Privacy</h3>
            <ul>
              <li>Control what data is logged</li>
              <li>Exclude sensitive PII by default</li>
              <li>Use transform functions for filtering</li>
              <li>Audit external service access</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="hooks-library-section">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}><BookOpenIcon /> Learn More</h2>
        <p>
          For detailed implementation guides, check out:
          <br />
          • Source code: <code>packages/hooks/src/</code>
          • Development History: <code>packages/hooks/DevelopmentHistoryLog.md</code>
          • Hooks vs Pipelines: See HooksPage.tsx for comparison
          </p>
        </div>
      </div>
    </ErrorBoundary>
  );
}
